import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const colors = {
    primary: '#fddb1c', // Yellow/Gold
    black: '#0a0a0a',
    blackLight: '#1a1a1a',
    white: '#ffffff',
    gray: '#666666',
    grayLight: '#999999'
  };

  const styles = {
    footer: {
      background: colors.black,
      color: colors.white,
      padding: isMobile ? '30px 0 20px' : '50px 0 30px', // REDUCED padding
      borderTop: `1px solid ${colors.primary}20`,
      position: 'relative',
      overflow: 'hidden'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 24px',
      position: 'relative',
      zIndex: 2
    },
    bgPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 20% 50%, ${colors.primary}10 0%, transparent 50%)`,
      pointerEvents: 'none',
      zIndex: 1
    },
    footerGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : '1.5fr 1fr 1.5fr',
      gap: isMobile ? '30px' : '30px', // REDUCED gap
      marginBottom: isMobile ? '30px' : '40px' // REDUCED margin
    },
    footerCol: {
      textAlign: isMobile ? 'center' : 'left'
    },
    logoSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'center' : 'flex-start'
    },
    // Logo styles - BIGGER
    logo: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      marginBottom: '12px', // REDUCED margin
      height: isMobile ? '70px' : '90px', // INCREASED height
      maxWidth: '350px', // INCREASED max-width
      cursor: 'pointer'
    },
    logoImage: {
      height: '100%',
      width: 'auto',
      maxWidth: '300px', // INCREASED max-width
      maxHeight: isMobile ? '70px' : '90px', // INCREASED max-height
      objectFit: 'contain'
    },
    // Text fallback for logo - BIGGER
    logoText: {
      fontSize: isMobile ? '32px' : '42px', // INCREASED font size
      fontWeight: 800,
      color: colors.white,
      letterSpacing: '-1px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      lineHeight: 1
    },
    logoSpan: {
      color: colors.primary,
      textTransform: 'lowercase',
      fontWeight: 400,
      fontSize: isMobile ? '28px' : '36px' // INCREASED font size
    },
    logoDesc: {
      color: colors.grayLight,
      fontSize: isMobile ? '13px' : '14px', // REDUCED font size
      lineHeight: 1.5, // REDUCED line height
      maxWidth: isMobile ? '100%' : '280px', // REDUCED max-width
      marginTop: '5px' // REDUCED margin
    },
    footerColH4: {
      marginBottom: isMobile ? '15px' : '18px', // REDUCED margin
      fontSize: isMobile ? '16px' : '15px', // REDUCED font size
      color: colors.primary,
      fontWeight: 600,
      position: 'relative',
      display: 'inline-block',
      paddingBottom: '6px', // REDUCED padding
      borderBottom: `2px solid ${colors.primary}`
    },
    footerColUl: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    footerColLi: {
      marginBottom: '10px' // REDUCED margin
    },
    footerColLink: {
      color: colors.grayLight,
      fontSize: isMobile ? '14px' : '13px', // REDUCED font size
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      display: 'inline-block',
      position: 'relative',
      cursor: 'pointer'
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px', // REDUCED gap
      marginBottom: '12px', // REDUCED margin
      justifyContent: isMobile ? 'center' : 'flex-start',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      color: 'inherit'
    },
    contactIcon: {
      color: colors.primary,
      fontSize: '16px', // REDUCED font size
      width: '20px', // REDUCED width
      textAlign: 'center'
    },
    contactText: {
      color: colors.grayLight,
      fontSize: isMobile ? '13px' : '14px', // REDUCED font size
      lineHeight: 1.4, // REDUCED line height
      transition: 'color 0.3s ease',
      wordBreak: 'break-word'
    },
    mapBox: {
      height: isMobile ? '150px' : '180px', // REDUCED height
      background: colors.blackLight,
      borderRadius: '12px', // REDUCED border radius
      overflow: 'hidden',
      marginTop: '15px', // REDUCED margin
      border: `1px solid ${colors.primary}30`,
      transition: 'all 0.3s ease',
      width: '100%'
    },
    mapIframe: {
      width: '100%',
      height: '100%',
      border: 'none'
    },
    footerBottom: {
      borderTop: `1px solid ${colors.primary}20`,
      paddingTop: isMobile ? '15px' : '20px', // REDUCED padding
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: isMobile ? '15px' : '0', // REDUCED gap
      fontSize: isMobile ? '11px' : '12px', // REDUCED font size
      color: colors.gray
    },
    copyright: {
      textAlign: isMobile ? 'center' : 'left',
      order: isMobile ? 2 : 1
    }
  };

  // Main navigation links
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Rental', path: '/prices' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  // Updated contact details with new phone, email and location
  const contactDetails = [
    { 
      icon: 'fa-map-marker-alt', 
      text: 'Subramaniya Siva Street, Chennai',
      href: 'https://maps.google.com/?q=Subramaniya+siva+street+Chennai',
      type: 'location'
    },
    { 
      icon: 'fa-phone-alt', 
      text: '+91 86376 58382',
      href: 'tel:+918637658382',
      type: 'phone'
    },
    { 
      icon: 'fa-envelope', 
      text: 'sahatravels966@gmail.com',
      href: 'mailto:sahatravels966@gmail.com',
      type: 'email'
    }
  ];

  // Handle logo click
  const handleLogoClick = () => {
    scrollToTop();
  };

  // Handle navigation click
  const handleNavClick = () => {
    scrollToTop();
  };

  // Handle logo error
  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <footer style={styles.footer}>
      {/* Decorative Background */}
      <div style={styles.bgPattern}></div>

      <div style={styles.container}>
        <div style={styles.footerGrid}>
          {/* Company Info - WITH BIGGER LOGO */}
          <div style={styles.footerCol}>
            <div style={styles.logoSection}>
              <Link to="/" style={styles.logo} onClick={handleLogoClick}>
                {!logoError ? (
                  <img 
                    src="/saha-logo.png"
                    alt="SAHA Travels" 
                    style={styles.logoImage}
                    onError={handleLogoError}
                  />
                ) : (
                  <div style={styles.logoText}>
                    SAHA<span style={styles.logoSpan}>travels</span>
                  </div>
                )}
              </Link>
              <p style={styles.logoDesc}>
                Moving India forward. From daily commutes to dream vacations, we've got you covered with premium travel solutions.
              </p>
            </div>
          </div>

          {/* Navigation Links */}
          <div style={styles.footerCol}>
            <h4 style={styles.footerColH4}>Quick Links</h4>
            <ul style={styles.footerColUl}>
              {navLinks.map((link, index) => (
                <li key={index} style={styles.footerColLi}>
                  <Link 
                    to={link.path} 
                    style={styles.footerColLink}
                    onClick={handleNavClick}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = colors.primary;
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.grayLight;
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Map Combined */}
          <div style={styles.footerCol}>
            <h4 style={styles.footerColH4}>Get in Touch</h4>
            
            {/* Contact Details - Clickable */}
            {contactDetails.map((item, index) => (
              <a
                key={index}
                href={item.href}
                style={styles.contactItem}
                target={item.type === 'location' ? '_blank' : '_self'}
                rel={item.type === 'location' ? 'noopener noreferrer' : ''}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateX(5px)';
                    const textSpan = e.currentTarget.querySelector('span');
                    if (textSpan) textSpan.style.color = colors.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateX(0)';
                    const textSpan = e.currentTarget.querySelector('span');
                    if (textSpan) textSpan.style.color = colors.grayLight;
                  }
                }}
              >
                <i className={`fas ${item.icon}`} style={styles.contactIcon}></i>
                <span style={styles.contactText}>{item.text}</span>
              </a>
            ))}

            {/* Map with updated Chennai location */}
            <div 
              style={styles.mapBox}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.boxShadow = `0 8px 20px ${colors.primary}30`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = `${colors.primary}30`;
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7774.110112130676!2d80.197595!3d13.032166!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267006653c9eb%3A0xdff8381770ab7cbd!2sSubramaniya%20siva%20street!5e0!3m2!1sen!2sin!4v1771866976778!5m2!1sen!2sin" 
                title="SAHA Travels Chennai Office"
                style={styles.mapIframe}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={styles.footerBottom}>
          <div style={styles.copyright}>
            <p>© {new Date().getFullYear()} SAHA Technology India Pvt Ltd. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Additional Styles */}
      <style>{`
        @media (max-width: 480px) {
          .footer-grid {
            gap: 30px !important;
          }
          
          div[style*="contactItem"] {
            justify-content: center;
          }
        }

        /* Smooth transitions */
        a, button {
          transition: all 0.3s ease;
        }

        /* Map container hover effect on desktop only */
        @media (min-width: 769px) {
          div[style*="mapBox"]:hover {
            transform: scale(1.02);
            border-color: ${colors.primary};
          }
        }

        /* Touch-friendly targets */
        a, button {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          min-height: 44px;
        }

        /* Responsive map */
        iframe {
          max-width: 100%;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Ensure icons are visible */
        .fas {
          font-family: "Font Awesome 6 Free";
          font-weight: 900;
        }

        /* Remove default link styles */
        a {
          text-decoration: none;
          color: inherit;
        }

        /* Logo hover effect */
        div[style*="logo"]:hover {
          opacity: 0.9;
        }
      `}</style>
    </footer>
  );
};

export default Footer;